import {fuzzyMatchWithMapping} from '@wandb/weave/common/util/fuzzyMatch';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import produce from 'immer';
import _ from 'lodash';
import React, {useCallback, useMemo, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {StrictInputProps} from 'semantic-ui-react';

import iconCheck from '../../assets/ic-check.svg';
import iconCheckGray from '../../assets/ic-check-gray.svg';
import iconLockClosed from '../../assets/ic-lock-closed.svg';
import {
  useProfilePageShowcasedProjectsQuery,
  useTeamPageShowcasedProjectsQuery,
} from '../../generated/graphql';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {profileProjectShowcased} from '../../services/analytics';
import {accessIsPublicOrOpen, AccessOptions} from '../../util/permissions';
import {
  ShowcasedProject,
  useEntityPageContext,
  useEntityPageUpdaterContext,
} from './context';
import * as S from './CustomizeShowcaseModal.styles';
import {getShowcasedProject, ProjectData} from './util';

type ShowcaseCandidate = ShowcasedProject & {isPublic: boolean};

type CustomizeShowcasedProjectsModalTeamProps = {
  entityName: string;
  onClose: () => void;
};

export const CustomizeShowcasedProjectsModalTeam: React.FC<CustomizeShowcasedProjectsModalTeamProps> =
  React.memo(({entityName, onClose}) => {
    const [pattern, onProjectFilterChange] = useState('');
    const teamPageProjects = useTeamPageShowcasedProjectsQuery({
      variables: {entityName, pattern, first: 20},
    });

    const allProjects =
      teamPageProjects.data?.projects?.edges.map(p => p.node as ProjectData) ??
      [];

    return (
      <CustomizeShowcasedProjectsModal
        allProjects={allProjects}
        onProjectFilterChange={onProjectFilterChange}
        onClose={onClose}
      />
    );
  });

type CustomizeShowcasedProjectsModalUserProps = {
  username: string;
  onClose: () => void;
};

export const CustomizeShowcasedProjectsModalUser: React.FC<CustomizeShowcasedProjectsModalUserProps> =
  React.memo(({username, onClose}) => {
    const [pattern, onProjectFilterChange] = useState('');
    const profilePageProjectsQuery = useProfilePageShowcasedProjectsQuery({
      variables: {username, pattern, first: 20},
    });

    const allProjects =
      profilePageProjectsQuery.data?.user?.projects?.edges.map(
        p => p.node as ProjectData
      ) ?? [];

    return (
      <CustomizeShowcasedProjectsModal
        allProjects={allProjects}
        onProjectFilterChange={onProjectFilterChange}
        onClose={onClose}
      />
    );
  });

type CustomizeShowcasedProjectsModalProps = {
  allProjects: ProjectData[];
  onProjectFilterChange: (filter: string) => void;
  onClose: () => void;
};

const CustomizeShowcasedProjectsModal: React.FC<CustomizeShowcasedProjectsModalProps> =
  React.memo(({allProjects, onProjectFilterChange, onClose}) => {
    const {entityName, showcasedProjectIDs} = useEntityPageContext();
    const {setShowcasedProjectIDs} = useEntityPageUpdaterContext();

    const projects: ShowcaseCandidate[] = useMemo(
      () =>
        allProjects
          .map(getShowcaseCandidateFromProjectData)
          .filter(isNotNullOrUndefined),
      [allProjects]
    );

    const showcasedProjectIDSet: Set<string> = useMemo(
      () => new Set(showcasedProjectIDs),
      [showcasedProjectIDs]
    );
    // This is the exact same as above but doesn't update.
    // We use this for sorting the options.
    // The sort shouldn't jump around when new items are selected.
    const showcasedProjectIDSetStatic: Set<string> = useMemo(
      () => new Set(showcasedProjectIDs),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const showcaseProjectID = useCallback(
      (projectID: string) => {
        profileProjectShowcased({entityName, projectID});
        setShowcasedProjectIDs(prev =>
          produce(prev, draft => {
            draft.push(projectID);
          })
        );
      },
      [entityName, setShowcasedProjectIDs]
    );

    const unshowcaseProjectID = useCallback(
      (projectID: string) => {
        setShowcasedProjectIDs(prev =>
          produce(prev, draft => {
            const i = draft.findIndex(id => id === projectID);
            if (i > -1) {
              draft.splice(i, 1);
            }
          })
        );
      },
      [setShowcasedProjectIDs]
    );

    const toggleShowcase = useCallback(
      (projectID: string) => {
        if (showcasedProjectIDSet.has(projectID)) {
          unshowcaseProjectID(projectID);
        } else {
          showcaseProjectID(projectID);
        }
      },
      [showcaseProjectID, unshowcaseProjectID, showcasedProjectIDSet]
    );

    const [filter, setFilter] = useState(``);

    const filteredProjects = useMemo(() => {
      if (!filter) {
        return projects;
      }
      return fuzzyMatchWithMapping(projects, filter, r => r.name);
    }, [projects, filter]);

    const sortedProjects = useMemo(() => {
      if (filter) {
        // Fuzzy match filtering already applies its own sort
        // which we don't want to override.
        return filteredProjects;
      }

      const selectedProjects = sortByRunCountDescending(
        filteredProjects.filter(r => showcasedProjectIDSetStatic.has(r.id))
      );

      const availableProjects = sortByRunCountDescending(
        filteredProjects.filter(
          r => !showcasedProjectIDSetStatic.has(r.id) && r.isPublic
        )
      );

      const disabledProjects = sortByRunCountDescending(
        filteredProjects.filter(r => !r.isPublic)
      );

      return [...selectedProjects, ...availableProjects, ...disabledProjects];
    }, [filter, filteredProjects, showcasedProjectIDSetStatic]);

    return (
      <S.Modal open onClose={onClose}>
        <S.Header>Customize your featured projects</S.Header>
        <S.Subheader>
          Select those projects you'd like others to see when they view your
          profile.
        </S.Subheader>
        <S.LoadedContent>
          <S.FilterInput
            placeholder="Filter projects"
            icon={<S.SearchIcon />}
            value={filter}
            onChange={
              ((e, {value}) => {
                onProjectFilterChange?.(value);
                setFilter(value);
              }) as StrictInputProps[`onChange`]
            }
          />
          <S.Options>
            {sortedProjects.map(r => {
              const option = (
                <S.Option
                  key={r.id}
                  selected={showcasedProjectIDSet.has(r.id)}
                  disabled={!r.isPublic}
                  onClick={() => {
                    if (r.isPublic) {
                      toggleShowcase(r.id);
                    }
                  }}>
                  <S.Name>
                    {r.entityName}/{r.name}
                  </S.Name>
                  <S.Details>
                    {r.user} - {r.runCount} runs
                  </S.Details>
                  <S.SrcIcon
                    src={
                      showcasedProjectIDSet.has(r.id)
                        ? iconCheck
                        : r.isPublic
                          ? iconCheckGray
                          : iconLockClosed
                    }
                  />
                </S.Option>
              );
              return r.isPublic ? (
                option
              ) : (
                <S.DisabledPopup
                  key={r.id}
                  basic
                  inverted
                  position="top center"
                  popperModifiers={{
                    preventOverflow: {
                      boundariesElement: 'viewport',
                    },
                  }}
                  trigger={option}
                  content={
                    <S.DisabledPopupContent>
                      <S.DisabledPopupIcon />
                      This project is private
                    </S.DisabledPopupContent>
                  }
                />
              );
            })}
          </S.Options>
          <S.CloseButton onClick={onClose}>Done</S.CloseButton>
        </S.LoadedContent>
      </S.Modal>
    );
  });

function getShowcaseCandidateFromProjectData(
  p: ProjectData
): ShowcaseCandidate | null {
  const showcasedProject = getShowcasedProject(p);
  if (showcasedProject == null) {
    return null;
  }

  return {
    ...showcasedProject,
    isPublic: accessIsPublicOrOpen(p.access as AccessOptions),
  };
}

function sortByRunCountDescending(
  items: ShowcaseCandidate[]
): ShowcaseCandidate[] {
  return _.sortBy(items, i => -i.runCount);
}
